import { Checkbox } from "@/components/ui/checkbox";
import { DEPTH_COPY, type DepthChoice, KIND_COPY, PRESETS, PRESET_ORDER, type ScopeKind, kindsOf, presetOfKinds } from "@/lib/grant-scopes";
import { cn } from "@/lib/utils";
import { Check, Minus } from "lucide-react";
import { useEffect, useRef, useState } from "react";

// How deep one Boo learns from another: three named presets from shallow to
// deep, or Custom by kind. The source's admins narrow it item by item when
// they approve. Custom kinds that add up to a preset select it, and the
// checkboxes stay open until a preset is clicked or focus leaves them.

const KINDS: ScopeKind[] = ["skills", "org", "channels", "people"];

export function DepthPicker({ value, onChange, disabled }: { value: DepthChoice; onChange: (v: DepthChoice) => void; disabled?: boolean }) {
  const [editing, setEditing] = useState(false);
  const pressed = usePressedTarget();
  const customOpen = value.depth === "custom" || editing;
  return (
    <div className="flex flex-col gap-2" role="radiogroup" aria-label="How much to share">
      <div className="grid gap-2 sm:grid-cols-3">
        {PRESET_ORDER.map((p) => {
          const kinds = kindsOf(PRESETS[p]);
          const active = value.depth === p;
          return (
            <button
              key={p}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={disabled}
              onClick={() => {
                setEditing(false);
                onChange({ ...value, depth: p });
              }}
              className={cn(
                "flex flex-col gap-2 rounded-lg border p-3 text-left transition-[colors,transform] duration-100 motion-safe:active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active ? "border-foreground bg-muted/60 shadow-[inset_0_0_0_1px_var(--foreground)]" : "border-border hover:bg-muted/30",
              )}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">{DEPTH_COPY[p].label}</span>
                <DepthMeter depth={p} />
              </span>
              <ul className="mt-auto flex flex-col gap-0.5 text-xs">
                {KINDS.map((k) => (
                  <li key={k} className={cn("flex items-center gap-1.5", kinds[k] ? "text-foreground" : "text-muted-foreground/60")}>
                    {kinds[k] ? <Check className="size-3" aria-hidden /> : <Minus className="size-3" aria-hidden />}
                    <span>{KIND_COPY[k].label}</span>
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>
      <div
        className={cn(
          "rounded-lg border transition-colors",
          value.depth === "custom" ? "border-foreground bg-muted/60 shadow-[inset_0_0_0_1px_var(--foreground)]" : "border-border",
        )}
        onBlur={(e) => {
          if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
          // A press inside (a checkbox's label text takes no focus) keeps it open.
          const press = pressed.current;
          if (press && e.currentTarget.contains(press)) return;
          // Folding shifts what's below; during a click, wait for its release so the click lands where it was aimed.
          if (press) window.addEventListener("pointerup", () => setEditing(false), { once: true });
          else setEditing(false);
        }}
      >
        <button
          type="button"
          role="radio"
          aria-checked={value.depth === "custom"}
          disabled={disabled}
          onClick={() => {
            setEditing(true);
            onChange({ ...value, depth: "custom" });
          }}
          className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
        >
          <span className="text-sm font-medium">{DEPTH_COPY.custom.label}</span>
          {value.depth !== "custom" && customOpen && <span className="text-xs text-muted-foreground">Same as {DEPTH_COPY[value.depth].label}</span>}
        </button>
        {customOpen && (
          <div className="grid gap-1 border-t border-border px-3 py-2.5 sm:grid-cols-2">
            {KINDS.map((k) => (
              <label key={k} className="flex cursor-pointer items-start gap-2 rounded-md p-1.5 text-sm hover:bg-background/60" title={KIND_COPY[k].hint}>
                <Checkbox
                  className="mt-0.5"
                  checked={value.custom[k]}
                  disabled={disabled}
                  onCheckedChange={(v) => {
                    const custom = { ...value.custom, [k]: v === true };
                    setEditing(true);
                    onChange({ depth: presetOfKinds(custom) ?? "custom", custom });
                  }}
                />
                <span>{KIND_COPY[k].label}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Where a mouse button or finger is pressed on the page right now; null when none is. */
function usePressedTarget() {
  const down = useRef<Node | null>(null);
  useEffect(() => {
    const press = (e: PointerEvent) => (down.current = e.target as Node | null);
    const release = () => (down.current = null);
    window.addEventListener("pointerdown", press, true);
    window.addEventListener("pointerup", release, true);
    window.addEventListener("pointercancel", release, true);
    return () => {
      window.removeEventListener("pointerdown", press, true);
      window.removeEventListener("pointerup", release, true);
      window.removeEventListener("pointercancel", release, true);
    };
  }, []);
  return down;
}

/** Three bars, filled to the depth: a quick visual for shallow to deep. */
export function DepthMeter({ depth, className }: { depth: string | null | undefined; className?: string }) {
  const level = depth === "essentials" ? 1 : depth === "team" ? 2 : depth === "full" ? 3 : 0;
  return (
    <span className={cn("flex items-end gap-0.5", className)} aria-hidden>
      {[1, 2, 3].map((i) => (
        <span key={i} className={cn("w-1 rounded-sm", i === 1 ? "h-1.5" : i === 2 ? "h-2.5" : "h-3.5", i <= level ? "bg-foreground" : "bg-muted-foreground/25")} />
      ))}
    </span>
  );
}
